import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import MainLayout from "../../Layout/MainLayout";
import VoucherHeader from "./sections/VoucherList/VoucherHeader";
import VoucherTable from "./sections/VoucherList/VoucherTable";
import { getProxyUrl } from "../../utils/assetUtils";
import { useAgent } from "../../context/agent/AgentContext";
import { useSubscription } from "../../context/subscription/SubscriptionContext";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import PingModal from "../../components/modals/PingModal";
import PageContainer from "../../components/layout/PageContainer";
import PDFVoucherView from "./sections/Shared/PDFVoucherView";
import { agentService } from "../../services/agent/agentService";
import { voucherService } from "../../services/vouchers/voucherService";
import TableSkeleton from "../../components/feedback/TableSkeleton";
import vouchersData from "../../data/vouchers/vouchersData.json";

const voucherColumns = vouchersData.voucherEmptyState?.columns || [];

const VoucherPage = () => {
    const { agent, voucherTheme } = useAgent();
    const { 
        vouchersCreated, 
        cycleStartDate, 
        refreshSubscription, 
        loading: subscriptionLoading 
    } = useSubscription();
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState("all");
    const [datas, setDatas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [localSearchTerm, setLocalSearchTerm] = useState("");
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'danger',
        showCancel: true,
        confirmText: 'Delete',
        cancelText: 'Cancel'
    });
    const [pingModal, setPingModal] = useState({
        isOpen: false,
        bookingId: null,
        guestName: null
    });

    // Agent details for PDF generation
    const [logo, setLogo] = useState("");
    const [agentName, setAgentName] = useState("");
    const [agentEmail, setAgentEmail] = useState("");
    const [agentPhone, setAgentPhone] = useState("");
    const [agentAddress, setAgentAddress] = useState("");
    const [city, setCity] = useState("");
    const [pincode, setPincode] = useState("");

    // Dynamic PDF generator state
    const [pdfGeneratingVoucher, setPdfGeneratingVoucher] = useState(null);
    const pdfRef = useRef(null);

    useEffect(() => {
        if (agent) {
            setAgentName(agent.company_name || '');
            setAgentEmail(agent.email || '');
            setAgentPhone(agent.phone_number || '');
            setAgentAddress(agent.address || '');
            setCity(agent.city || '');
            setPincode(agent.pincode || '');

            if (agent.logo) {
                const fetchLogo = async () => {
                    try {
                        const proxyUrl = getProxyUrl(agent.logo);
                        const res = await fetch(proxyUrl);
                        const blob = await res.blob();
                        const reader = new FileReader();
                        reader.onloadend = () => setLogo(reader.result);
                        reader.readAsDataURL(blob);
                    } catch (err) {
                        setLogo(agent.logo);
                    }
                };
                fetchLogo();
            }
        }
    }, [agent]);

    const fetchVouchers = async () => {
        try {
            setLoading(true);
            const data = await voucherService.getVouchers();
            const list = Array.isArray(data) ? data : (data?.vouchers || []);
            const vouchers = list.map(v => ({
                id: v._id,
                clientName: v.guest_information?.guest_name || "",
                bookingID: v.booking_id,
                phoneNumber: v.guest_information?.contact_number || "",
                personalMessage: v.important_notes?.[0] || "",
                itineraryLink: v.itenary_link,
                status: v.status,
                createdAt: v.createdAt,
                destination: v.tour_info?.destinations?.map(d => d.city || d.location_name).filter(Boolean).join(', ') || "N/A",
                tour_info: v.tour_info,
                guest_information: v.guest_information,
                hotel_bookings: v.hotel_bookings,
                travel_bookings: v.travel_bookings,
                itinerary: v.itinerary,
                contact_details: v.contact_details,
                important_notes: v.important_notes,
                source_documents: v.source_documents,
                rawVoucher: v
            }));
            setDatas(vouchers);
        } catch (err) {
            console.error("Failed to fetch vouchers", err);
            if (err.response?.status === 400) navigate('/login');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVouchers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEditClick = (voucherId) => {
        navigate(`/voucher-edit/${voucherId}`);
    };

    const handleDeleteClick = (voucherId) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Voucher',
            message: 'Are you sure you want to delete this voucher? This action cannot be undone.',
            type: 'danger',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            showCancel: true,
            onConfirm: () => executeDeleteVoucher(voucherId)
        });
    };

    const executeDeleteVoucher = async (voucherId) => {
        try {
            setLoading(true);
            await voucherService.deleteVoucher(voucherId);
            toast.success("Voucher deleted successfully!");
            fetchVouchers();
            if (typeof refreshSubscription === 'function') {
                refreshSubscription();
            }
        } catch (err) {
            console.error("Failed to delete voucher", err);
            toast.error("Failed to delete voucher.");
        } finally {
            setLoading(false);
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
    };

    const generateVoucherPdfBlob = async (voucherData) => {
        setPdfGeneratingVoucher(voucherData);
        // Wait for React to render the hidden container
        await new Promise((resolve) => setTimeout(resolve, 150));

        const MARGIN = 28;
        const container = pdfRef.current;
        if (!container) throw new Error("PDF container element not ready");

        const fullCanvas = await html2canvas(container, {
            scale: 2.5,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
        });

        const pdf = new jsPDF("p", "pt", "a4");
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        const contentW = pageW - MARGIN * 2;
        const contentH = pageH - MARGIN * 2;

        const canvasToPagePt = contentW / fullCanvas.width;
        const pageHeightPx = contentH / canvasToPagePt;
        const containerTop = container.getBoundingClientRect().top;

        const lastPageEl = container.querySelector('[data-pdf-section="last-page"]');
        let lastPageTop = fullCanvas.height;
        if (lastPageEl) {
            const calculatedTop = Math.round((lastPageEl.getBoundingClientRect().top - containerTop) * 2.5);
            if (calculatedTop > 100 && calculatedTop < fullCanvas.height) {
                lastPageTop = calculatedTop;
            }
        }

        const mainContentEnd = lastPageEl && lastPageTop < fullCanvas.height ? lastPageTop : fullCanvas.height;

        const sections = container.querySelectorAll('[data-pdf-section]');
        const sectionBreaks = [0];
        sections.forEach(sec => {
            if (sec === lastPageEl) return;
            const relTop = Math.round((sec.getBoundingClientRect().top - containerTop) * 2.5);
            if (relTop > 0 && relTop < mainContentEnd) sectionBreaks.push(relTop);
        });
        sectionBreaks.push(mainContentEnd);

        const pageSlices = [];
        let pageStart = 0;

        while (pageStart < mainContentEnd) {
            const idealEnd = pageStart + pageHeightPx;

            if (idealEnd >= mainContentEnd) {
                pageSlices.push({ start: pageStart, end: mainContentEnd });
                break;
            }

            let bestBreak = idealEnd;
            for (let i = sectionBreaks.length - 1; i >= 0; i--) {
                if (sectionBreaks[i] <= idealEnd && sectionBreaks[i] > pageStart && sectionBreaks[i] < mainContentEnd) {
                    bestBreak = sectionBreaks[i];
                    break;
                }
            }

            if (bestBreak <= pageStart) {
                bestBreak = idealEnd;
            }

            pageSlices.push({ start: pageStart, end: bestBreak });
            pageStart = bestBreak;
        }

        // Ensure last-page is always its own dedicated standalone page
        if (lastPageEl && lastPageTop < fullCanvas.height) {
            pageSlices.push({ start: lastPageTop, end: fullCanvas.height, isLastPage: true });
        }

        pageSlices.forEach((slice, idx) => {
            if (idx > 0) pdf.addPage();

            const sliceH = slice.end - slice.start;
            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = fullCanvas.width;
            sliceCanvas.height = sliceH;
            const ctx = sliceCanvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            ctx.drawImage(fullCanvas, 0, -slice.start);

            let sliceHPt = sliceH * canvasToPagePt;
            let drawW = contentW;
            let drawH = sliceHPt;
            let offsetX = MARGIN;
            let offsetY = MARGIN;

            if (slice.isLastPage || (lastPageEl && idx === pageSlices.length - 1)) {
                if (sliceHPt > contentH) {
                    const scale = contentH / sliceHPt;
                    drawW = contentW * scale;
                    drawH = contentH;
                    offsetX = MARGIN + (contentW - drawW) / 2;
                }
            }

            pdf.addImage(sliceCanvas.toDataURL('image/jpeg', 1.0), 'JPEG', offsetX, offsetY, drawW, drawH);

            if (slice.isLastPage || (lastPageEl && idx === pageSlices.length - 1)) {
                const voucherItem = container.getAttribute('data-booking-id') || 'a1b2c';
                const playStoreUrl = `https://play.google.com/store/apps/details?id=live.trrip.app&hl=en_IN&bookingId=${voucherItem}`;
                const appStoreUrl = `https://apps.apple.com/us/app/trrip-your-ontour-assistant/id6759519854`;

                pdf.link(
                    offsetX + drawW * 0.052,
                    offsetY + drawH * 0.745,
                    drawW * 0.220,
                    drawH * 0.055,
                    { url: playStoreUrl }
                );

                pdf.link(
                    offsetX + drawW * 0.052,
                    offsetY + drawH * 0.805,
                    drawW * 0.220,
                    drawH * 0.055,
                    { url: appStoreUrl }
                );
            }
        });

        return pdf;
    };

    const handleDownload = async (voucherId, fileName) => {
        const toastId = toast.loading("Downloading voucher...");
        try {
            const voucherItem = datas.find(d => d.id === voucherId)?.rawVoucher;

            if (voucherItem) {
                const pdf = await generateVoucherPdfBlob(voucherItem);
                const downloadFileName = fileName || `${voucherItem.guest_information?.guest_name || 'Voucher'}_Voucher.pdf`;
                pdf.save(downloadFileName);
                toast.dismiss(toastId);
                toast.success("Voucher PDF downloaded!");
                return;
            }

            const pdfUrl = voucherItem?.itenary_link || voucherItem?.itineraryLink;
            if (pdfUrl && pdfUrl.startsWith('http') && pdfUrl !== 'pending' && pdfUrl !== 'pending...') {
                try {
                    const res = await fetch(pdfUrl);
                    const blob = await res.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = fileName || `${voucherItem?.guest_information?.guest_name || 'Voucher'}_Voucher.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(blobUrl);
                    toast.dismiss(toastId);
                    toast.success("Voucher PDF downloaded!");
                    return;
                } catch (fetchErr) {
                    console.warn("Direct S3 fetch download failed, falling back", fetchErr);
                }
            }

            // Fallback to server endpoint if local raw voucher is missing
            const data = await voucherService.downloadVoucherPdf(voucherId, 'download');
            const blob = new Blob([data], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName || 'Voucher.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            toast.dismiss(toastId);
            toast.success("Voucher PDF downloaded!");
        } catch (error) {
            toast.dismiss(toastId);
            console.error("Download failed:", error);
            toast.error("Download failed.");
        } finally {
            setPdfGeneratingVoucher(null);
        }
    };

    const handleViewVoucher = async (voucherId, directUrl = null) => {
        const toastId = toast.loading("Opening voucher...");
        try {
            const voucherItem = datas.find(d => d.id === voucherId)?.rawVoucher;
            const pdfUrl = directUrl || voucherItem?.itenary_link || voucherItem?.itineraryLink;

            if (pdfUrl && pdfUrl.startsWith('http') && pdfUrl !== 'pending' && pdfUrl !== 'pending...') {
                window.open(pdfUrl, '_blank', 'noopener,noreferrer');
                toast.dismiss(toastId);
                return;
            }

            if (voucherItem) {
                const pdf = await generateVoucherPdfBlob(voucherItem);
                const pdfBlob = pdf.output('blob');
                const blobUrl = URL.createObjectURL(pdfBlob);
                window.open(blobUrl, '_blank');
                toast.dismiss(toastId);
                return;
            }

            const data = await voucherService.downloadVoucherPdf(voucherId, 'view');
            const blob = new Blob([data], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
            toast.dismiss(toastId);
        } catch (error) {
            toast.dismiss(toastId);
            console.error("View failed:", error);
            toast.error("View failed.");
        } finally {
            setPdfGeneratingVoucher(null);
        }
    };

    const handlePingUser = (bookingId, guestName = null) => {
        setPingModal({
            isOpen: true,
            bookingId: bookingId,
            guestName: guestName
        });
    };

    const executePingUser = async (bookingId, message, guestName = null) => {
        try {
            setLoading(true);
            await voucherService.pingTraveller({
                booking_id: bookingId,
                message: message,
                guest_name: guestName
            });
            toast.success("Push notification sent!");
        } catch (err) {
            console.error("Failed to ping user", err);
            const errMsg = err.response?.data?.message;
            if (errMsg && (errMsg.includes('registered devices') || errMsg.includes('No traveller') || errMsg.includes('no registered devices'))) {
                toast.error("No traveller logged in - ask traveller to download app");
            } else {
                toast.error(errMsg || "No traveller logged in - ask traveller to download app");
            }
        } finally {
            setLoading(false);
            setPingModal(prev => ({ ...prev, isOpen: false }));
        }
    };

    const generateAndCopyMessage = (voucher) => {
        let guestName = voucher.clientName || 'Guest';
        const bookingId = voucher.bookingID || "";
        const androidLink = `https://play.google.com/store/apps/details?id=live.trrip.app&hl=en_IN&bookingId=${bookingId}`;
        const iosLink = `https://apps.apple.com/in/app/trrip-your-ontour-assistant/id6759519854`;

        const message = `Hello ${guestName},
Here are your trip details!

*Login Credentials:*
*Your Name:* Enter your name
*Booking ID:* ${bookingId}

📲 *Download App to view Voucher:*
*Android:* ${androidLink}
*iOS:* ${iosLink}

Have a wonderful trip!`;

        navigator.clipboard.writeText(message).then(() => {
            localStorage.setItem('has_shared_app', 'true');
            localStorage.setItem('trrip_step_share_completed', 'true');
            agentService.markAppShared();
            toast.success("Message copied!");
        });
    };

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const filteredData = datas.filter((item) => {
        const matchesSearch = localSearchTerm.trim() === "" || 
            Object.values(item).some(val => val?.toString().toLowerCase().includes(localSearchTerm.toLowerCase()));
        const matchesStatus = statusFilter === "all" || item.status === statusFilter;
        return matchesSearch && matchesStatus;
    }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [localSearchTerm, statusFilter]);

    // Pagination Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentVouchers = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <MainLayout activeTab="voucher">
            <PageContainer>
                <VoucherHeader 
                    onOpenCustomizer={() => navigate('/voucher-template', { state: { returnUrl: '/voucher' } })}
                />

                {loading ? (
                    <TableSkeleton columns={voucherColumns} rows={5} />
                ) : (
                    <VoucherTable 
                        currentVouchers={currentVouchers}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        paginate={paginate}
                        totalItems={filteredData.length}
                        handleViewVoucher={handleViewVoucher}
                        generateAndCopyMessage={generateAndCopyMessage}
                        handleDownload={handleDownload}
                        handleEditClick={handleEditClick}
                        handleDeleteClick={handleDeleteClick}
                        handlePingUser={handlePingUser}
                        localSearchTerm={localSearchTerm}
                        setLocalSearchTerm={setLocalSearchTerm}
                        statusFilter={statusFilter}
                        handleStatusChange={(e) => setStatusFilter(e.target.value)}
                    />
                )}

                <ConfirmationModal
                    isOpen={confirmModal.isOpen}
                    onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                    onConfirm={confirmModal.onConfirm}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    type={confirmModal.type}
                    confirmText={confirmModal.confirmText}
                    cancelText={confirmModal.cancelText}
                    showCancel={confirmModal.showCancel}
                />

                <PingModal 
                    isOpen={pingModal.isOpen}
                    onClose={() => setPingModal(prev => ({ ...prev, isOpen: false }))}
                    onConfirm={(message) => executePingUser(pingModal.bookingId, message, pingModal.guestName)}
                    bookingId={pingModal.bookingId}
                    guestName={pingModal.guestName}
                />

                {/* Hidden PDF Generator Container for List Downloads */}
                <div style={{ position: 'absolute', top: 0, left: '-9999px', width: '794px', background: 'white' }}>
                    <div ref={pdfRef}>
                        {pdfGeneratingVoucher && (
                            <PDFVoucherView
                                data={pdfGeneratingVoucher}
                                bookingId={pdfGeneratingVoucher?.booking_id || ''}
                                logo={logo}
                                agentName={agentName}
                                agentEmail={agentEmail}
                                agentPhone={agentPhone}
                                agentAddress={agentAddress}
                                city={city}
                                pincode={pincode}
                                customTheme={
                                    pdfGeneratingVoucher?.voucher_theme
                                        ? {
                                            isCustom: Boolean(pdfGeneratingVoucher.voucher_theme.is_custom || pdfGeneratingVoucher.voucher_theme.isCustom),
                                            primaryColor: pdfGeneratingVoucher.voucher_theme.primary_color || pdfGeneratingVoucher.voucher_theme.primaryColor || '#0d9488',
                                            secondaryColor: pdfGeneratingVoucher.voucher_theme.secondary_color || pdfGeneratingVoucher.voucher_theme.secondaryColor || '#1d4ed8',
                                            textColor: pdfGeneratingVoucher.voucher_theme.text_color || pdfGeneratingVoucher.voucher_theme.textColor || '#111827',
                                            headerBgColor: pdfGeneratingVoucher.voucher_theme.header_bg_color || pdfGeneratingVoucher.voucher_theme.headerBgColor || '#ffffff',
                                            include_itinerary: Boolean(pdfGeneratingVoucher.voucher_theme.include_itinerary)
                                        }
                                        : (voucherTheme || { isCustom: false, primaryColor: '#0d9488', secondaryColor: '#1d4ed8', textColor: '#111827', headerBgColor: '#ffffff', include_itinerary: false })
                                }
                            />
                        )}
                    </div>
                </div>
            </PageContainer>
        </MainLayout>
    );
};

export default VoucherPage;
